import { useSaveMatterSurveyDraft } from "@/lib/data/react-query/matters/matter-survey";
import type { MatterSurveyAnswerSession } from "@/lib/data/requests/matters/matter-survey";
import type { APIError } from "@/lib/types/APIResponse";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export type SurveyDraftAnswer = string | string[] | null;
export type SurveyDraftSaveStatus = "idle" | "saving" | "saved" | "error";

function normalizedAnswer(value: SurveyDraftAnswer): SurveyDraftAnswer {
  if (Array.isArray(value) && !value.length) return null;
  if (typeof value === "string" && !value.trim()) return null;
  return value;
}

function sameAnswer(left: SurveyDraftAnswer, right: SurveyDraftAnswer) {
  left = normalizedAnswer(left);
  right = normalizedAnswer(right);
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((option, index) => option === right[index])
    );
  }
  return left === right;
}

function initialAnswers(form: MatterSurveyAnswerSession) {
  return Object.fromEntries(
    form.questions.map((question) => [
      question.id,
      Object.prototype.hasOwnProperty.call(form.draftAnswers, question.id)
        ? form.draftAnswers[question.id]
        : (question.answer ?? question.prefillValue),
    ]),
  ) as Record<string, SurveyDraftAnswer>;
}

function saveErrorMessage(cause: unknown) {
  const response = isAxiosError(cause)
    ? (cause.response?.data as Partial<APIError> | undefined)
    : undefined;
  return response?.meta?.userMsg ?? "Could not save these answers. Try again.";
}

export function useStaffSurveyDraft(
  form: MatterSurveyAnswerSession | null,
  resetKey = 0,
) {
  const { mutateAsync: saveDraft } = useSaveMatterSurveyDraft();
  const [answers, setAnswers] = useState(() =>
    form ? initialAnswers(form) : ({} as Record<string, SurveyDraftAnswer>),
  );
  const answersRef = useRef(answers);
  const savedRef = useRef(answers);
  const revisionRef = useRef(form?.draftRevision ?? 0);
  const persistedRef = useRef(new Set(Object.keys(form?.draftAnswers ?? {})));
  const initializedRef = useRef(form ? `${form.id}:${resetKey}` : null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const reloadRequiredRef = useRef(false);
  const [reloadRequired, setReloadRequired] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveStatus, setSaveStatus] = useState<
    Record<string, SurveyDraftSaveStatus>
  >(() =>
    Object.fromEntries(
      (form?.questions ?? []).map((question) => [
        question.id,
        persistedRef.current.has(question.id) ? "saved" : "idle",
      ]),
    ),
  );

  useEffect(() => {
    if (!form) return;
    const key = `${form.id}:${resetKey}`;
    if (initializedRef.current === key) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const initial = initialAnswers(form);
    answersRef.current = initial;
    savedRef.current = initial;
    revisionRef.current = form.draftRevision;
    persistedRef.current = new Set(Object.keys(form.draftAnswers));
    reloadRequiredRef.current = false;
    initializedRef.current = key;
    setAnswers(initial);
    setReloadRequired(false);
    setSaveError("");
    setSaveStatus(
      Object.fromEntries(
        form.questions.map((question) => [
          question.id,
          persistedRef.current.has(question.id) ? "saved" : "idle",
        ]),
      ),
    );
  }, [form, resetKey]);

  const flush = useCallback((): Promise<number> => {
    if (!form) return Promise.reject(new Error("Survey is still loading."));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;

    const task = queueRef.current
      .catch(() => {})
      .then(async () => {
        if (reloadRequiredRef.current) {
          throw new Error("Reload the latest survey answers before saving.");
        }
        for (;;) {
          const changes = form.questions
            .filter(
              (question) =>
                !sameAnswer(
                  answersRef.current[question.id],
                  savedRef.current[question.id],
                ),
            )
            .map((question) => ({
              questionId: question.id,
              value: normalizedAnswer(answersRef.current[question.id]),
            }));
          if (!changes.length) return revisionRef.current;

          try {
            const result = await saveDraft({
              surveyId: form.id,
              roundId: form.roundId,
              answers: changes,
              draftRevision: revisionRef.current,
            });
            revisionRef.current = result.data.draftRevision;
            setSaveError("");
            for (const change of changes) {
              savedRef.current[change.questionId] =
                result.data.draftAnswers[change.questionId] ?? null;
              persistedRef.current.add(change.questionId);
              if (
                sameAnswer(
                  answersRef.current[change.questionId],
                  savedRef.current[change.questionId],
                )
              ) {
                setSaveStatus((current) => ({
                  ...current,
                  [change.questionId]: "saved",
                }));
              }
            }
          } catch (cause) {
            const requiresReload =
              isAxiosError(cause) &&
              [409, 410].includes(cause.response?.status ?? 0);
            reloadRequiredRef.current = requiresReload;
            setReloadRequired(requiresReload);
            setSaveError(saveErrorMessage(cause));
            setSaveStatus((current) => ({
              ...current,
              ...Object.fromEntries(
                changes.map((change) => [change.questionId, "error"]),
              ),
            }));
            throw cause;
          }
        }
      });
    queueRef.current = task.then(
      () => {},
      () => {},
    );
    return task;
  }, [form, saveDraft]);

  const updateAnswer = useCallback(
    (questionId: string, value: SurveyDraftAnswer, debounceMs: number) => {
      answersRef.current = { ...answersRef.current, [questionId]: value };
      setAnswers(answersRef.current);
      if (reloadRequiredRef.current) return;

      const unchanged = sameAnswer(value, savedRef.current[questionId]);
      setSaveStatus((current) => ({
        ...current,
        [questionId]: unchanged
          ? persistedRef.current.has(questionId)
            ? "saved"
            : "idle"
          : "saving",
      }));
      setSaveError("");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void flush().catch(() => {});
      }, debounceMs);
    },
    [flush],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    ready: Boolean(form && initializedRef.current === `${form.id}:${resetKey}`),
    answers,
    updateAnswer,
    flush,
    saveStatus,
    saveError,
    reloadRequired,
  };
}
